import { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Divider,
  CircularProgress, Alert,
} from '@mui/material';
import Iconify from '../../../../components/Iconify';

const ACTION_LABELS = {
  book_meeting_room: { icon: 'mdi:calendar-clock', label: 'Book Meeting Room', color: 'primary' },
  add_member: { icon: 'mdi:account-plus', label: 'Add Member', color: 'success' },
  add_company: { icon: 'mdi:domain-plus', label: 'Add Company', color: 'info' },
  resolve_service_request: { icon: 'mdi:check-circle', label: 'Resolve Request', color: 'warning' },
};

export default function WriteConfirmBlock({ block, conversationId, onConfirm }) {
  const { action, title, details = {} } = block;
  const confirmPayload = block.confirm_payload;
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const meta = ACTION_LABELS[action] || { icon: 'mdi:check', label: 'Confirm Action', color: 'primary' };

  const handleConfirm = async () => {
    if (!onConfirm || !confirmPayload) return;
    setLoading(true);
    setError(null);
    try {
      const result = await onConfirm(conversationId, confirmPayload.function, confirmPayload.args);
      if (result.success) {
        setDone(true);
      } else {
        setError(result.error || 'Action failed');
      }
    } catch (e) {
      setError(e.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Alert severity="success" icon={<Iconify icon="mdi:check-circle" />} sx={{ my: 1 }}>
        Action completed successfully.
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        my: 1.5,
        p: 2,
        borderRadius: 2,
        borderColor: `${meta.color}.main`,
        borderWidth: 1.5,
        maxWidth: 420,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon={meta.icon} sx={{ color: `${meta.color}.main`, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>{title || meta.label}</Typography>
        </Stack>

        <Divider />

        <Box>
          {Object.entries(details).map(([key, val]) => {
            if (!val && val !== 0) return null;
            const label = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            return (
              <Stack key={key} direction="row" justifyContent="space-between" sx={{ py: 0.4 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="caption" fontWeight={600}>
                  {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                </Typography>
              </Stack>
            );
          })}
        </Box>

        {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            color={meta.color}
            size="small"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : <Iconify icon="mdi:check" />}
            onClick={handleConfirm}
          >
            {loading ? 'Processing...' : 'Confirm'}
          </Button>
          <Button variant="outlined" size="small" color="inherit" disabled={loading} onClick={() => setDone(true)}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
